import { useLocaleFormatters } from "$root/localization/hooks";
import { builtInCaseInsensitiveSortFor, qualifier } from "$root/utils";
import {
	ActionText,
	AutoSortTable,
	Banner,
	Icon,
	type TableColumn,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import { IconButton, NumberInput } from "@mdotm/shadcn/mdotm/components";
import { useMultiSelect } from "@mdotm/shadcn/legacy/headless";
import { overrideClassName, toClassListRecord } from "@mdotm/shadcn/legacy/react-extensions";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import BigNumber from "bignumber.js";
import { type ReactNode, useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { UseCompositionBuilderResult } from "../universe/composition";
import type { MinimumViableInstrument } from "./InstrumentsSelectorTable";

export type InstrumentsWeightsTable<T extends MinimumViableInstrument> = {
	instruments: T[];
	tableTitle?: ReactNode;
	onRemoveInstrument?(ticker: string): void;
	compositionBuilder: UseCompositionBuilderResult;
};

export function InstrumentsWeightsTable<T extends MinimumViableInstrument>({
	instruments: rows,
	tableTitle,
	onRemoveInstrument,
	compositionBuilder,
}: InstrumentsWeightsTable<T>): React.JSX.Element {
	const weightsMultiSelectCtx = useMultiSelect<string>();
	const { t } = useTranslation();

	const refs = useUnsafeUpdatedRef({ weightsMultiSelectCtx, onRemoveInstrument });
	useEffect(() => {
		refs.current.weightsMultiSelectCtx.setSelection(
			refs.current.weightsMultiSelectCtx.selection.intersect(rows.map((r) => r.ticker ?? "")),
		);
	}, [rows, refs]);

	const {
		column: checkBoxColumn,
		rowClassName,
		toggle,
	} = useSelectableTableColumn({
		rows,
		multiSelectCtx: weightsMultiSelectCtx,
		selectBy: ({ ticker }) => ticker ?? "",
		mode: "checkbox",
	});

	const { formatNumber } = useLocaleFormatters();
	const sumOfWeights = compositionBuilder.getTotalWeight();
	const columns = useMemo<TableColumn<T>[]>(
		() => [
			checkBoxColumn,
			{
				header: "index",
				content: ({ instrument }) => instrument,
				sortFn: builtInCaseInsensitiveSortFor("assetClass"),
				name: "assetClass",
			},
			{
				header: "weight",
				align: "end",
				content: ({ ticker }, cellProps) => (
					<div className={overrideClassName(cellProps.className, "flex items-center")} style={cellProps.style}>
						<NumberInput
							onClick={(e) => e.stopPropagation()}
							min={0}
							max={100}
							step={BigNumber(10).pow(-compositionBuilder.getDecimalPlaces()).toNumber()}
							inputAppearance={{
								className: "text-right",
							}}
							size="x-small"
							className="w-full"
							value={compositionBuilder.getWeight(ticker ?? "")?.toNumber() ?? null}
							onChange={(value) => compositionBuilder.setWeight(ticker ?? "", value === null ? null : BigNumber(value))}
							rightContent={<Icon icon="Percentile" />}
							data-qualifier={qualifier.instrumentClassificationDialog.instrumentWeightsTable.columns.input(ticker!)}
						/>
					</div>
				),
				sortFn: (a, b) =>
					compositionBuilder.getWeight(a.ticker || "")?.comparedTo(compositionBuilder.getWeight(b.ticker || "") ?? 0) ??
					0,
				name: "weight",
				footer: () => `${formatNumber(sumOfWeights.toNumber(), compositionBuilder.getDecimalPlaces())}%`,
				maxWidth: 130,
				minWidth: 130,
			},
			{
				name: "actions",
				header: "",
				content: ({ ticker }, cellProps) => (
					<div
						className={overrideClassName(cellProps.className, "flex items-center justify-center")}
						style={cellProps.style}
					>
						<IconButton
							unstyled
							onClick={() => {
								refs.current.onRemoveInstrument?.(ticker ?? "");
							}}
							data-qualifier={qualifier.instrumentClassificationDialog.instrumentWeightsTable.columns.delete(ticker!)}
							size={24}
							color={themeCSSVars.global_palette_primary_600}
							icon="Delete"
						/>
					</div>
				),
				minWidth: 38,
				maxWidth: 38,
			},
		],
		[checkBoxColumn, compositionBuilder, formatNumber, sumOfWeights, refs],
	);
	return (
		<>
			{tableTitle && <div>{tableTitle}</div>}
			<AutoSortTable
				rows={rows}
				rowClassName={(row, rowIndex) => ({
					...toClassListRecord(rowClassName(row, rowIndex)),
					InstrumentEditorTableRow: true,
				})}
				columns={columns}
				className="max-h-[410px]"
				onRowClick={({ ticker }) => toggle(ticker ?? "")}
			/>
			{!sumOfWeights.eq(100) && rows.length > 0 && (
				<Banner
					title={
						sumOfWeights.lt(100)
							? t("EDIT_COMPOSITION.NORMALIZE_BOX.LESS_THAN_100.TITLE")
							: t("EDIT_COMPOSITION.NORMALIZE_BOX.MORE_THAN_100.TITLE")
					}
					severity="warning"
				>
					<Trans
						i18nKey={
							sumOfWeights.lt(100)
								? "EDIT_COMPOSITION.NORMALIZE_BOX.LESS_THAN_100.DESCRIPTION_NORMALISE"
								: "EDIT_COMPOSITION.NORMALIZE_BOX.MORE_THAN_100.DESCRIPTION_NORMALISE"
						}
						components={{
							action: (
								<ActionText
									disabled={weightsMultiSelectCtx.selection.size === 0}
									onClick={() => {
										if (weightsMultiSelectCtx.selection.size > 0) {
											compositionBuilder.normalise(weightsMultiSelectCtx.selection);
										}
									}}
									data-qualifier={qualifier.instrumentClassificationDialog.instrumentWeightsTable.normalise}
								>
									Normalise
								</ActionText>
							),
						}}
					/>
				</Banner>
			)}
		</>
	);
}
